import { useState } from 'react';
import { Link } from 'react-router-dom';
import { BarChart3, Bike, Clock, IndianRupee, Package, PieChart, Star, Sparkles } from 'lucide-react';
import { SalesGlance } from './SalesGlance';
import { cn as cnClasses } from '@/lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { SectionHeader } from '@/components/charts/SectionHeader';
import { KpiTile } from '@/components/charts/KpiTile';
import { DeltaBadge } from '@/components/charts/DeltaBadge';
import { EmptyState } from '@/components/charts/EmptyState';
import { HourlyHeatmap } from '@/components/charts/HourlyHeatmap';
import { ChannelMix } from '@/components/charts/ChannelMix';
import { MotionCard } from '@/components/motion/MotionCard';
import { useDashboard } from '@/hooks/useDashboardContext';
import { useMetric } from '@/hooks/useMetric';
import { useTopItems, type ItemMetric } from '@/hooks/useTopItems';
import { useTopCategories } from '@/hooks/useTopCategories';
import { useHourlyHeatmap } from '@/hooks/useHourlyHeatmap';
import { useChannelMix } from '@/hooks/useChannelMix';
import { useChannelSplit } from '@/hooks/useChannelSplit';
import { DeliveryDineinSplit } from '@/components/charts/DeliveryDineinSplit';
import { fmtISTRange, formatINRCompact, formatNumberCompact } from '@/lib/format';
import { cn } from '@/lib/utils';

export function SalesPage() {
  const [tab, setTab] = useState<'glance' | 'detail'>('glance');
  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2 border-b border-border/40">
        <TabButton active={tab === 'glance'} onClick={() => setTab('glance')}>
          <Sparkles className="mr-1.5 h-3.5 w-3.5 text-emerald-500" /> Glance
        </TabButton>
        <TabButton active={tab === 'detail'} onClick={() => setTab('detail')}>
          <BarChart3 className="mr-1.5 h-3.5 w-3.5 text-muted-foreground" /> Detail
        </TabButton>
      </div>
      {tab === 'glance' ? <SalesGlance /> : <SalesDetail />}
    </div>
  );
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cnClasses(
        'inline-flex items-center px-3 py-2 text-sm font-medium border-b-2 -mb-px transition-colors',
        active
          ? 'border-primary text-foreground'
          : 'border-transparent text-muted-foreground hover:text-foreground',
      )}
    >
      {children}
    </button>
  );
}

function SalesDetail() {
  const { period, outletId } = useDashboard();
  const [itemBy, setItemBy] = useState<ItemMetric>('units');
  const [catBy, setCatBy] = useState<'units' | 'gross_sales'>('gross_sales');

  const revenue = useMetric({ outletId, source: 'net_sales', period });
  const orders = useMetric({ outletId, source: 'orders', period });
  const avgTicket = useMetric({ outletId, source: 'avg_ticket', period });
  const topItems = useTopItems({ outletId, period, by: itemBy, limit: 10 });
  const topCats = useTopCategories({ outletId, period, by: catBy, limit: 10 });
  const heatmap = useHourlyHeatmap({ outletId, period });
  const channels = useChannelMix({ outletId, period });
  const split = useChannelSplit({ outletId, period });

  return (
    <div className="space-y-8">
      <SectionHeader
        title={
          <span className="flex items-center gap-2">
            <BarChart3 className="h-5 w-5 text-muted-foreground" />
            Sales
          </span>
        }
        description={
          revenue.current
            ? `${revenue.current.label} · ${fmtISTRange(revenue.current.start, revenue.current.end)} vs ${fmtISTRange(revenue.previous.start, revenue.previous.end)}`
            : ''
        }
      />

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <KpiTile
          label="Net revenue"
          icon={<IndianRupee className="h-3.5 w-3.5" />}
          metric={revenue.data?.metric}
          loading={revenue.isLoading}
          format={formatINRCompact}
          sparkline={revenue.data?.trend.map((p) => p.current)}
          comparisonRange={{ start: revenue.previous.start, end: revenue.previous.end }}
        />
        <KpiTile
          label="Orders"
          icon={<Package className="h-3.5 w-3.5" />}
          metric={orders.data?.metric}
          loading={orders.isLoading}
          format={formatNumberCompact}
          sparkline={orders.data?.trend.map((p) => p.current)}
          comparisonRange={{ start: orders.previous.start, end: orders.previous.end }}
          delay={0.05}
        />
        <KpiTile
          label="Avg order value"
          icon={<IndianRupee className="h-3.5 w-3.5" />}
          metric={avgTicket.data?.metric}
          loading={avgTicket.isLoading}
          format={formatINRCompact}
          sparkline={avgTicket.data?.trend.map((p) => p.current)}
          comparisonRange={{ start: avgTicket.previous.start, end: avgTicket.previous.end }}
          delay={0.1}
        />
      </div>

      <MotionCard delay={0.15}>
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Bike className="h-4 w-4 text-muted-foreground" /> Delivery vs Dine-in
              <span className="ml-1 text-2xs font-normal text-muted-foreground">
                by gross sales
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <DeliveryDineinSplit
              delivery={split.delivery}
              dineIn={split.dineIn}
              pickUp={split.pickUp}
              totalSales={split.totalSales}
              loading={split.isLoading}
              compact
            />
          </CardContent>
        </Card>
      </MotionCard>

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
        <MotionCard>
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="h-4 w-4 text-muted-foreground" /> Hourly order volume
                <span className="ml-1 text-2xs font-normal text-muted-foreground">
                  avg per weekday × hour
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <HourlyHeatmap
                matrix={heatmap.data?.matrix ?? []}
                max={heatmap.data?.max ?? 0}
                loading={heatmap.isLoading}
              />
            </CardContent>
          </Card>
        </MotionCard>
        <MotionCard delay={0.05}>
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <PieChart className="h-4 w-4 text-muted-foreground" /> Channel mix
                <span className="ml-1 text-2xs font-normal text-muted-foreground">
                  by gross sales
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ChannelMix rows={channels.data?.rows ?? []} loading={channels.isLoading} />
            </CardContent>
          </Card>
        </MotionCard>
      </div>

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
        <MotionCard>
          <Card>
            <CardHeader className="flex-row items-center justify-between space-y-0 pb-3">
              <CardTitle>Best-selling items</CardTitle>
              <Toggle
                value={itemBy}
                options={[
                  { id: 'units', label: 'Units' },
                  { id: 'gross_sales', label: 'Sales' },
                  { id: 'orders', label: 'Orders' },
                ]}
                onChange={setItemBy as (v: string) => void}
              />
            </CardHeader>
            <CardContent className="p-0">
              {topItems.isLoading ? (
                <SkeletonRows />
              ) : topItems.data && topItems.data.rows.length > 0 ? (
                <table className="w-full text-sm">
                  <thead className="border-b border-border/60 bg-muted/30 text-2xs uppercase text-muted-foreground">
                    <tr>
                      <th className="px-3 py-2 text-left font-medium">#</th>
                      <th className="px-3 py-2 text-left font-medium">Item</th>
                      <th className="px-3 py-2 text-right font-medium">
                        {itemBy === 'units' ? 'Units' : itemBy === 'gross_sales' ? 'Sales' : 'Orders'}
                      </th>
                      <th className="px-3 py-2 text-right font-medium">vs prev</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border/40">
                    {topItems.data.rows.map((r, i) => (
                      <tr key={r.itemId} className="hover:bg-muted/30">
                        <td className="px-3 py-2 text-2xs text-muted-foreground">{i + 1}</td>
                        <td className="px-3 py-2">
                          <Link
                            to={`/sales/items/${encodeURIComponent(r.itemId)}`}
                            className="font-medium leading-tight hover:underline"
                          >
                            {r.itemName}
                          </Link>
                          {r.categoryName && (
                            <div className="mt-0.5 text-2xs text-muted-foreground">
                              {r.categoryName}
                            </div>
                          )}
                        </td>
                        <td className="px-3 py-2 text-right font-mono tabular-nums">
                          {itemBy === 'gross_sales'
                            ? formatINRCompact(r.metric.current)
                            : formatNumberCompact(r.metric.current)}
                        </td>
                        <td className="px-3 py-2 text-right">
                          <DeltaBadge
                            deltaPct={r.metric.deltaPct}
                            comparable={r.metric.comparable}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="p-6">
                  <EmptyState
                    title="No item data"
                    description="No items sold in this period — or the mv_item_daily refresh hasn't run."
                  />
                </div>
              )}
            </CardContent>
          </Card>
        </MotionCard>

        <MotionCard delay={0.05}>
          <Card>
            <CardHeader className="flex-row items-center justify-between space-y-0 pb-3">
              <CardTitle>Best-performing categories</CardTitle>
              <Toggle
                value={catBy}
                options={[
                  { id: 'gross_sales', label: 'Sales' },
                  { id: 'units', label: 'Units' },
                ]}
                onChange={setCatBy as (v: string) => void}
              />
            </CardHeader>
            <CardContent className="p-0">
              {topCats.isLoading ? (
                <SkeletonRows />
              ) : topCats.data && topCats.data.rows.length > 0 ? (
                <table className="w-full text-sm">
                  <thead className="border-b border-border/60 bg-muted/30 text-2xs uppercase text-muted-foreground">
                    <tr>
                      <th className="px-3 py-2 text-left font-medium">Category</th>
                      <th className="px-3 py-2 text-right font-medium">
                        {catBy === 'gross_sales' ? 'Sales' : 'Units'}
                      </th>
                      <th className="px-3 py-2 text-right font-medium">Avg price</th>
                      <th className="px-3 py-2 text-right font-medium">vs prev</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border/40">
                    {topCats.data.rows.map((c) => (
                      <tr key={c.categoryName} className="hover:bg-muted/30">
                        <td className="px-3 py-2">
                          <div className="font-medium leading-tight">{c.categoryName}</div>
                          <div className="mt-0.5 text-2xs text-muted-foreground">
                            {c.itemCount} items · {formatNumberCompact(c.units)} units
                          </div>
                        </td>
                        <td className="px-3 py-2 text-right font-mono tabular-nums">
                          {catBy === 'gross_sales'
                            ? formatINRCompact(c.sales)
                            : formatNumberCompact(c.units)}
                        </td>
                        <td className="px-3 py-2 text-right font-mono tabular-nums text-muted-foreground">
                          {formatINRCompact(c.avgPrice)}
                        </td>
                        <td className="px-3 py-2 text-right">
                          <DeltaBadge
                            deltaPct={c.metric.deltaPct}
                            comparable={c.metric.comparable}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="p-6">
                  <EmptyState
                    title="No category data"
                    description="No category sales found for this period."
                  />
                </div>
              )}
            </CardContent>
          </Card>
        </MotionCard>
      </div>

      <MotionCard delay={0.1}>
        <Card>
          <CardHeader className="flex-row items-center justify-between space-y-0">
            <CardTitle className="flex items-center gap-2">
              <Star className="h-4 w-4" /> Item ratings from aggregators
            </CardTitle>
            <Badge variant="muted">Phase 3</Badge>
          </CardHeader>
          <CardContent>
            <EmptyState
              title="Wires once Food DB sync lands"
              description="Will pull per-item ratings + review counts from Swiggy and Zomato and join them onto this catalog so each best-seller carries its aggregator rating."
            />
          </CardContent>
        </Card>
      </MotionCard>
    </div>
  );
}

function Toggle<T extends string>({
  value,
  options,
  onChange,
}: {
  value: T;
  options: { id: T; label: string }[];
  onChange: (v: T) => void;
}) {
  return (
    <div className="inline-flex rounded-md border border-border/60 bg-muted/30 p-0.5 text-2xs">
      {options.map((o) => (
        <button
          key={o.id}
          type="button"
          onClick={() => onChange(o.id)}
          className={cn(
            'rounded px-2 py-1 transition-colors',
            value === o.id ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground',
          )}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

function SkeletonRows() {
  return (
    <div className="space-y-2 p-4">
      {[0, 1, 2, 3, 4].map((i) => (
        <Skeleton key={i} className="h-10 w-full" />
      ))}
    </div>
  );
}
